import { v4 } from "uuid";
import {
  type ResourceSeries,
  ResourceSeriesExecutionStage,
  type ResourceSeriesItem,
  type ResourceSeriesItemData,
  type ResourceSeriesRecord,
} from "./types";

/**
 * State slice that starts a new try of the current attempt. Needs to be spread
 * in a ResourceSeries on every try, including retries of the same item
 */
export const newTry = <AdditionalInfo = never>(): Pick<
  ResourceSeries<ResourceSeriesRecord, AdditionalInfo>,
  "comingNext" | "_startTimestamp" | "sendResults"
> => ({
  comingNext: undefined,
  _startTimestamp: Date.now(),
  sendResults: true,
});

/**
 * State slice that starts a new attempt at an item. Needs to be spread in a
 * ResourceSeries only when the current item changes — retries spread newTry
 * instead and stay within the same attempt
 */
export const newAttempt = <AdditionalInfo = never>(): Pick<
  ResourceSeries<ResourceSeriesRecord, AdditionalInfo>,
  | "attemptId"
  | "currentTry"
  | "_prevTriesResults"
  | "comingNext"
  | "_startTimestamp"
  | "sendResults"
> => ({
  ...newTry<AdditionalInfo>(),
  attemptId: v4(),
  currentTry: 1,
  _prevTriesResults: [],
});

export const placeholderResourceSeries = (entryId: string): ResourceSeries => ({
  ...newAttempt(),
  entryLearningSetId: entryId,
  resources: {},
  hierarchy: [],
  progression: 0,
  results: [],
  sendResults: false,
  executionStage: ResourceSeriesExecutionStage.PlayingCurrentExercise,
  additionalInformation: {
    totalItems: 1,
  },
});

export function getLearningItem<
  Data extends ResourceSeriesItemData,
  Context,
  AdditionalInfo,
>({
  resources,
  hierarchy,
}: Pick<
  ResourceSeries<ResourceSeriesRecord<Data, Context>, AdditionalInfo>,
  "resources" | "hierarchy"
>): ResourceSeriesItem<Data, Context> | undefined {
  if (!hierarchy) {
    return undefined;
  }
  const resource = resources[hierarchy[0]];
  if (!resource) {
    return undefined;
  }
  if (resource.type === "item") return resource;
  throw new Error(
    `Lowest hierarchy should be an item, not a set. Found ${resource.type} for ${resource.id}`,
  );
}

/** Experimentation series are pre or post tests */
export const isExperimentationSeries = ({
  resources,
  entryLearningSetId,
}: Pick<
  ResourceSeries<ResourceSeriesRecord, unknown>,
  "resources" | "entryLearningSetId"
>): boolean => {
  const entry = resources[entryLearningSetId];
  return entry?.type === "set" && entry.learning_set_type === "experimentation";
};

export const isResourceSeriesFinished = (
  resourceSeries: Pick<ResourceSeries, "executionStage">,
) =>
  resourceSeries.executionStage ===
  ResourceSeriesExecutionStage.ShowEndOfResourceSeries;
